import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    NgZone,
    OnInit,
    ViewEncapsulation,
} from "@angular/core";
import { S25DropdownAbstract } from "../../../s25-dropdown/s25.dropdown.abstract";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { BudgetService } from "../budget.service";
import { Budget } from "../Budget";
import { DropDownItem } from "../../../../pojo/DropDownItem";
import { S25DropdownPaginatedModule } from "../../../s25-dropdown/s25.dropdown.paginated.module";

@TypeManagerDecorator("s25-budget-category-dropdown")
@Component({
    selector: "s25-budget-category-dropdown",
    template: `
        @if (this.items) {
            <s25-generic-dropdown
                [chosen]="this.chosen"
                [hasFav]="false"
                [onSelect]="this.onSelect"
                [items]="this.items"
                [staticItems]="this.staticItems"
                [autoOpen]="this.autoOpen"
                [alwaysOpen]="this.alwaysOpen"
                [hideSearch]="this.hideSearch"
                [focusFirst]="true"
                [searchEnabled]="true"
                [apiBean]="this.apiBean"
                [placeholder]="this.placeholder || 'Select a Category'"
                [resetSelectedOnCleanup]="this.resetSelectedOnCleanup"
                [hasNoneItem]="this.hasNoneItem"
                [noneItemLabel]="this.noneItemLabel || 'No Category'"
                (chosenChange)="onChosenChange($event)"
            >
            </s25-generic-dropdown>
        }
    `,
    styles: `
        s25-budget-category-dropdown {
            position: relative;
            z-index: 1000;
        }

        /* Target the main dropdown container */
        s25-budget-category-dropdown .ui-select-container {
            position: relative;
            z-index: 1000;
        }

        /* Target the dropdown menu */
        s25-budget-category-dropdown .ui-select-dropdown,
        s25-budget-category-dropdown .select2-drop {
            position: absolute !important;
            z-index: 1001 !important;
            max-height: 300px !important;
            overflow-y: auto !important;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
            border: 1px solid #ddd !important;
            background: white !important;
            border-radius: 4px !important;
            min-width: 200px !important;
        }

        /* Ensure dropdown results list has proper styling */
        s25-budget-category-dropdown .select2-results {
            max-height: 280px !important;
            overflow-y: auto !important;
        }

        /* Ensure the dropdown doesn't get clipped by parent containers */
        s25-budget-category-dropdown .ngDropdownPaginated {
            position: relative;
            z-index: 1000;
        }
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: true,
    imports: [S25DropdownPaginatedModule],
})
export class S25BudgetCategoryDropdownComponent extends S25DropdownAbstract implements OnInit {
    @Input() organizationId?: number;
    @Input() includeSubcategories: boolean = true;
    @Input() activeOnly: boolean = true;

    constructor(
        private zone: NgZone,
        private cd: ChangeDetectorRef,
    ) {
        super();
    }

    ngOnInit() {
        this.loadCategories();
    }

    private async loadCategories() {
        try {
            const categories = await BudgetService.getBudgetCategories(this.organizationId);
            this.items = this.buildCategoryDropdownItems(categories);
            this.zone.run(() => {
                this.cd.detectChanges();
                this.onLoad && this.onLoad();
            });
        } catch (error) {
            console.error("Failed to load budget categories:", error);
            this.items = [];
            this.zone.run(() => {
                this.cd.detectChanges();
            });
        }
    }

    private buildCategoryDropdownItems(categories: Budget.BudgetCategory[]): DropDownItem[] {
        const items: DropDownItem[] = [];

        // Filter active categories if needed
        const filteredCategories = this.activeOnly ? categories.filter((cat) => !!cat.isActive) : categories;

        // Separate parent and child categories
        const parentCategories = filteredCategories.filter((cat) => !cat.parentCategoryId);
        const childCategories = filteredCategories.filter((cat) => cat.parentCategoryId);

        // Build hierarchical structure
        for (const parent of parentCategories) {
            // Add parent category
            items.push({
                val: parent.categoryId.toString(),
                txt: this.formatCategoryName(parent),
                itemId: parent.categoryId,
                itemName: parent.name,
                itemDesc: parent.description,
                data: parent,
            });

            // Add subcategories if enabled
            if (this.includeSubcategories) {
                const subcategories = childCategories.filter((cat) => cat.parentCategoryId === parent.categoryId);
                for (const subcategory of subcategories) {
                    items.push({
                        val: subcategory.categoryId.toString(),
                        txt: this.formatSubcategoryName(subcategory, parent),
                        itemId: subcategory.categoryId,
                        itemName: subcategory.name,
                        itemDesc: subcategory.description,
                        data: subcategory,
                    });
                }
            }
        }

        // Add orphaned subcategories (subcategories without valid parents)
        if (this.includeSubcategories) {
            const orphanedSubcategories = childCategories.filter(
                (cat) => !parentCategories.some((parent) => parent.categoryId === cat.parentCategoryId),
            );

            for (const orphan of orphanedSubcategories) {
                items.push({
                    val: orphan.categoryId.toString(),
                    txt: this.formatCategoryName(orphan),
                    itemId: orphan.categoryId,
                    itemName: orphan.name,
                    itemDesc: orphan.description,
                    data: orphan,
                });
            }
        }

        return items.sort((a, b) => a.txt.localeCompare(b.txt));
    }

    private formatCategoryName(category: Budget.BudgetCategory): string {
        let name = category.name;

        if (category.icon) {
            name = `${category.icon} ${name}`;
        }

        return name;
    }

    private formatSubcategoryName(subcategory: Budget.BudgetCategory, parent: Budget.BudgetCategory): string {
        let name = `├─ ${subcategory.name}`;

        if (subcategory.icon) {
            name = `├─ ${subcategory.icon} ${subcategory.name}`;
        }

        return name;
    }

    // Public method to refresh categories
    public refresh(): void {
        this.loadCategories();
    }

    // Helper method to get selected category data
    public getSelectedCategory(): Budget.BudgetCategory | null {
        return this.chosen?.data || null;
    }
}
